// src/app/gen2ai/resume-robot.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  ref,
  set,
  update,
  remove,
  onValue,
  DatabaseReference
} from 'firebase/database';
import { database } from '../gen2ai/fire-init';
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  User
} from 'firebase/auth';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

export type ResumeCategory =
  | 'Front-end'
  | 'Angular'
  | 'AI Dev'
  | 'Full-stack'
  | 'Python';

export interface ResumeItem {
  id: string;
  userId: string;

  title: string;
  description: string;

  fileUrl: string;
  fileType: 'pdf' | 'doc' | 'docx' | 'txt';
  storagePath: string;

  primaryCategory: ResumeCategory;
  labels: string[];

  pinned: boolean;
  archived: boolean;
  color: string;
  order: number;

  status: 'uploading' | 'ready' | 'enhancing' | 'error';

  enhancedText?: string;
  enhancedJsonRaw?: string | null;

  lastJobTitle?: string | null;
  lastJobUrl?: string | null;
  lastJobRoleLabel?: string | null;

  // Allow "json" as well so OutputFormat ('docx' | 'pdf' | 'gdoc' | 'json')
  // can be passed safely from the component.
  enhancedOutputFormat?: 'docx' | 'pdf' | 'gdoc' | 'json' | null;
  enhancedOutputUrl?: string | null;

  createdAt: number;
  updatedAt: number;
}

const REQUIRED_CATEGORIES: ResumeCategory[] = [
  'Front-end',
  'Angular',
  'AI Dev',
  'Full-stack',
  'Python'
];

@Injectable({ providedIn: 'root' })
export class ResumeRobotService {
  private auth = getAuth();
  private storage = getStorage();

  private resumesRef: DatabaseReference | null = null;

  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  private resumesSubject = new BehaviorSubject<ResumeItem[]>([]);
  resumes$ = this.resumesSubject.asObservable();

  private initialized = false;

  constructor() {
    this.initAuthListener();
  }

  /* ------------------------------------------------------------------ */
  /*  Auth                                                              */
  /* ------------------------------------------------------------------ */

  private initAuthListener(): void {
    if (this.initialized) return;
    this.initialized = true;

    onAuthStateChanged(this.auth, async (user) => {
      if (!user) {
        const cred = await signInAnonymously(this.auth);
        user = cred.user;
      }
      this.userSubject.next(user);
      this.attachResumesListener(user.uid);
    });
  }

  async signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(this.auth, provider);
  }

  async signOut(): Promise<void> {
    await this.auth.signOut();
    this.userSubject.next(null);
    this.resumesSubject.next([]);
    this.resumesRef = null;
  }

  private attachResumesListener(uid: string): void {
    this.resumesRef = ref(database, `resumes/${uid}`);
    onValue(this.resumesRef, (snapshot) => {
      const resumes: ResumeItem[] = [];
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach((id) => {
          resumes.push(data[id] as ResumeItem);
        });
      }
      resumes.sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return a.order - b.order;
      });
      this.resumesSubject.next(resumes);
    });
  }

  private ensureUserId(): string {
    const user = this.auth.currentUser;
    if (!user) throw new Error('User not initialized yet');
    return user.uid;
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                           */
  /* ------------------------------------------------------------------ */

  private validateCategory(primaryCategory: ResumeCategory): ResumeCategory {
    if (!REQUIRED_CATEGORIES.includes(primaryCategory)) {
      return 'AI Dev';
    }
    return primaryCategory;
  }

  private validateFileType(file: File): ResumeItem['fileType'] {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return ext as ResumeItem['fileType'];
    }
    throw new Error(
      'Unsupported file type. Please upload a PDF, DOC, DOCX, or TXT.'
    );
  }

  /* ------------------------------------------------------------------ */
  /*  CRUD + upload                                                     */
  /* ------------------------------------------------------------------ */

  async createResume(
    partial: {
      title: string;
      description: string;
      primaryCategory: ResumeCategory;
      extraLabels?: string[];
      color?: string;
      pinned?: boolean;
    },
    file: File
  ): Promise<string> {
    const uid = this.ensureUserId();
    const id = uuidv4();
    const now = Date.now();

    const primaryCategory = this.validateCategory(partial.primaryCategory);
    const fileType = this.validateFileType(file);

    const extraLabels = (partial.extraLabels || [])
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const labelsSet = new Set<string>([primaryCategory, ...extraLabels]);
    const labels = Array.from(labelsSet);

    const storagePath = `resumes/${uid}/${id}.${fileType}`;
    const sRef = storageRef(this.storage, storagePath);

    const resume: ResumeItem = {
      id,
      userId: uid,
      title: partial.title || '',
      description: partial.description || '',
      fileUrl: '',
      fileType,
      storagePath,
      primaryCategory,
      labels,
      pinned: !!partial.pinned,
      archived: false,
      color: partial.color || '#ffffff',
      order: now,
      status: 'uploading',
      enhancedText: undefined,
      enhancedJsonRaw: null,
      lastJobTitle: null,
      lastJobUrl: null,
      lastJobRoleLabel: null,
      enhancedOutputFormat: null,
      enhancedOutputUrl: null,
      createdAt: now,
      updatedAt: now
    };

    await set(ref(database, `resumes/${uid}/${id}`), resume);

    await uploadBytes(sRef, file, {
      contentType: file.type || 'application/octet-stream'
    });

    const downloadUrl = await getDownloadURL(sRef);

    await update(ref(database, `resumes/${uid}/${id}`), {
      fileUrl: downloadUrl,
      status: 'ready',
      updatedAt: Date.now()
    });

    return id;
  }

  async updateResume(id: string, changes: Partial<ResumeItem>): Promise<void> {
    const uid = this.ensureUserId();
    const now = Date.now();
    await update(ref(database, `resumes/${uid}/${id}`), {
      ...changes,
      updatedAt: now
    });
  }

  async deleteResume(id: string): Promise<void> {
    const uid = this.ensureUserId();
    await remove(ref(database, `resumes/${uid}/${id}`));
  }

  async togglePinned(id: string, pinned: boolean): Promise<void> {
    await this.updateResume(id, { pinned });
  }

  async toggleArchived(id: string, archived: boolean): Promise<void> {
    await this.updateResume(id, { archived });
  }

  async reorderResumes(orderKeys: string[]): Promise<void> {
    const uid = this.ensureUserId();
    const updates: any = {};
    orderKeys.forEach((id, idx) => {
      updates[`resumes/${uid}/${id}/order`] = idx + 1;
    });
    await update(ref(database), updates);
  }

  async setEnhancementStatus(
    id: string,
    status: ResumeItem['status'],
    enhancedText?: string,
    lastJobTitle?: string,
    lastJobUrl?: string,
    enhancedOutputFormat?: ResumeItem['enhancedOutputFormat'],
    enhancedOutputUrl?: string,
    lastJobRoleLabel?: string,
    enhancedJsonRaw?: string | null
  ): Promise<void> {
    const uid = this.ensureUserId();
    const now = Date.now();
    await update(ref(database, `resumes/${uid}/${id}`), {
      status,
      enhancedText: enhancedText ?? null,
      lastJobTitle: lastJobTitle ?? null,
      lastJobUrl: lastJobUrl ?? null,
      enhancedOutputFormat: enhancedOutputFormat ?? null,
      enhancedOutputUrl: enhancedOutputUrl ?? null,
      lastJobRoleLabel: lastJobRoleLabel ?? null,
      enhancedJsonRaw: enhancedJsonRaw ?? null,
      updatedAt: now
    });
  }
}
